import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight } from "lucide-react";

/**
 * PlusCTA
 * -----------------------------------------------------------------------------
 * Reusable jump-off strip that funnels free-product users to the WUA+
 * marketing page and, when it makes sense, straight to a specific intent
 * on the booking form.
 *
 * Two variants:
 *   - variant="strip"  → wide horizontal band suited to Landing / Shortlist
 *   - variant="inline" → compact card suited to Advanced Search / Degree Detail
 */
export default function PlusCTA({
  variant = "strip",
  eyebrow = "WhichUniversityAdvisor+",
  headline = "Ready for a counsellor who actually knows you?",
  body = "The free WUA is where every family should start. When you're ready for proactive AI (Anna), a verified Family Wallet, and human counsellors matched to your destination — WUA+ takes it from there.",
  primaryLabel = "See WUA+",
  primaryTo = "/plus",
  secondaryLabel = "Book a call",
  secondaryTo = "/wua-plus?intent=engaged",
  testId = "plus-cta",
}) {
  if (variant === "inline") {
    return (
      <Card
        data-testid={testId}
        className="p-5 border-[#4C1D95]/30 bg-gradient-to-br from-[#F5F0FF] to-white"
      >
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3 w-3 text-[#4C1D95]" />
          <div className="label-uppercase !text-[10px] text-[#4C1D95]">{eyebrow}</div>
        </div>
        <h3 className="font-editorial text-lg mt-1.5 leading-tight text-stone-900">{headline}</h3>
        <p className="text-[12px] text-stone-600 mt-2 leading-snug">{body}</p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button asChild size="sm" className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-full"
                  data-testid={`${testId}-primary`}>
            <Link to={primaryTo}>{primaryLabel} <ArrowRight className="h-3 w-3 ml-1" /></Link>
          </Button>
          <Button asChild size="sm" variant="outline" className="rounded-full"
                  data-testid={`${testId}-secondary`}>
            <Link to={secondaryTo}>{secondaryLabel}</Link>
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <section
      data-testid={testId}
      className="border-t border-b border-[#4C1D95]/20 bg-gradient-to-br from-[#F5F0FF] to-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-[1fr_auto] gap-8 items-center">
        <div className="max-w-2xl">
          <img
            src="/logo-plus.png"
            alt="WhichUniversityAdvisor+"
            data-testid={`${testId}-logo`}
            className="h-10 sm:h-12 w-auto mb-4"
          />
          <div className="inline-flex items-center gap-1.5 rounded-full border border-[#4C1D95]/30 bg-white px-3 py-1">
            <Sparkles className="h-3 w-3 text-[#4C1D95]" />
            <span className="label-uppercase !text-[10px] text-[#4C1D95]">{eyebrow}</span>
          </div>
          <h2 className="font-editorial text-2xl sm:text-3xl mt-3 leading-tight text-stone-900">
            {headline}
          </h2>
          <p className="mt-3 text-stone-700 max-w-xl">{body}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button asChild size="lg" className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-full px-5"
                  data-testid={`${testId}-primary`}>
            <Link to={primaryTo}>{primaryLabel} <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full px-5"
                  data-testid={`${testId}-secondary`}>
            <Link to={secondaryTo}>{secondaryLabel}</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
